import {
  Button,
  Checkbox,
  Divider,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
} from "antd";
import { IUser } from "../../types/interfaces";
import TextArea from "antd/es/input/TextArea";
import { INTERVALS } from "../../constants/constants";

interface Props {
  isModalOpen: boolean;
  handleOk: () => void;
  handleCancel: () => void;
  form: FormInstance<IUser>;
  loading?: boolean;
  editing?: boolean;
}

function SubscriptionPlanModal({
  isModalOpen,
  handleOk,
  handleCancel,
  form,
  loading,
  editing,
}: Props) {
  return (
    <Modal
      title={editing ? "Editar plan de suscripción" : "Agregar plan de suscripción"}
      open={isModalOpen}
      onOk={handleOk}
      width={520}
      closable={false}
      footer={[
        <Button
          key="submit"
          id={"submit"}
          type="primary"
          onClick={handleOk}
          loading={loading}
          style={{
            background: "#81BC3D",
            borderColor: "#81BC3D",
            borderRadius: "8px",
          }}
        >
          {editing ? "Actualizar plan" : "Crear plan"}
        </Button>,
        <Button key="back" onClick={handleCancel} style={{ borderRadius: "8px" }}>
          Cancelar
        </Button>,
      ]}
      closeIcon={false}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <div className={"modal-row"}>
          <Form.Item
            name="name"
            label="Nombre del plan"
            rules={[{ required: true, message: "Nombre de plan es requerido" }]}
            style={{ flex: 1 }}
          >
            <Input
              style={{ width: "100%", borderRadius: "8px" }}
              placeholder="Ej: Plan Profesional"
            />
          </Form.Item>
          <Form.Item
            name="frequency"
            label="Recurrencia"
            rules={[{ required: true, message: "Seleccione una recurrencia" }]}
            style={{ flex: 1 }}
          >
            <Select
              allowClear
              style={{ width: "100%" }}
              options={INTERVALS}
              placeholder="Frecuencia de cobro"
            />
          </Form.Item>
        </div>

        <Form.Item
          name="description"
          label="Descripción"
          rules={[{ required: true, message: "Descripción es requerida" }]}
        >
          <TextArea
            showCount
            placeholder="Describe las características del plan..."
            rows={3}
            maxLength={250}
            style={{ borderRadius: "8px" }}
          />
        </Form.Item>

        <div className={"modal-row"}>
          <Form.Item
            name="price"
            label="Precio (USD)"
            rules={[{ required: true, message: "Precio es requerido" }]}
            style={{ flex: 1 }}
          >
            <InputNumber
              style={{ width: "100%", borderRadius: "8px" }}
              prefix={"$"}
              min={0}
              step={0.01}
              placeholder="29.99"
            />
          </Form.Item>
          <Form.Item
            name="status"
            label="Estado"
            rules={[{ required: true, message: "Estado es requerido" }]}
            style={{ flex: 1 }}
          >
            <Select
              allowClear
              style={{ width: "100%" }}
              options={[
                { value: "active", label: "Activo" },
                { value: "inactive", label: "Inactivo" },
              ]}
              placeholder="Estado del plan"
            />
          </Form.Item>
        </div>

        <Form.Item
          name={"userLimit"}
          label="Límite de usuarios"
          rules={[{ required: true, message: "Límite de usuarios es requerido" }]}
        >
          <InputNumber
            style={{ width: "100%", borderRadius: "8px" }}
            min={1}
            max={1000}
            placeholder="Máximo de usuarios permitidos"
          />
        </Form.Item>

        <Divider style={{ margin: "12px 0" }} />

        <Form.Item
          name={"permissions"}
          label={"Permisos incluidos en el plan"}
          style={{ width: "100%" }}
        >
          <Checkbox.Group style={{ width: "100%" }}>
            <div style={{ width: "100%" }}>
              <h4 style={{ color: "#1677ff", fontSize: "13px", marginBottom: "6px" }}>
                🗺️ Mapas
              </h4>
              <Space direction={"horizontal"} style={{ marginBottom: "8px" }}>
                <Checkbox value={"ui:map:point"}>Mapas por punto</Checkbox>
                <Checkbox value={"ui:map:transect"}>Análisis por transecto</Checkbox>
              </Space>

              <Divider style={{ margin: "8px 0" }} />

              <h4 style={{ color: "#52c41a", fontSize: "13px", marginBottom: "6px" }}>
                🌎 Boletines Oceanográficos
              </h4>

              {/* Perú */}
              <div style={{ paddingLeft: 8, marginBottom: 10 }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: 4 }}>
                  🇵🇪 Perú
                </div>
                <Space direction="horizontal" wrap style={{ marginBottom: "4px" }}>
                  <Checkbox value={"ui:newsletter:peru_1"}>Boletín 1</Checkbox>
                  <Checkbox value={"ui:newsletter:peru_2"}>Boletín v3</Checkbox>
                </Space>
              </div>

              {/* Ecuador */}
              <div style={{ paddingLeft: 8, marginBottom: 10 }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: 4 }}>
                  🇪🇨 Ecuador
                </div>
                <Space direction="horizontal" wrap style={{ marginBottom: "4px" }}>
                  <Checkbox value={"ui:newsletter:ecuador"}>Boletín 1</Checkbox>
                  <Checkbox value={"ui:newsletter:ecuador_2"}>Boletín v3</Checkbox>
                </Space>
              </div>

              {/* Omán */}
              <div style={{ paddingLeft: 8, marginBottom: 10 }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: 4 }}>
                  🇴🇲 Omán
                </div>
                <Space direction="horizontal" wrap style={{ marginBottom: "4px" }}>
                  <Checkbox value={"ui:newsletter:oman"}>Boletín 1</Checkbox>
                  <Checkbox value={"ui:newsletter:oman_2"}>Boletín v3</Checkbox>
                </Space>
              </div>

              {/* Panamá */}
              <div style={{ paddingLeft: 8, marginBottom: 4 }}>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: 4 }}>
                  🇵🇦 Panamá
                </div>
                <Space direction="horizontal" wrap>
                  <Checkbox value={"ui:newsletter:panama"}>Boletín 1</Checkbox>
                  <Checkbox value={"ui:newsletter:panama_2"}>Boletín v3</Checkbox>
                </Space>
              </div>

            </div>
          </Checkbox.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default SubscriptionPlanModal;
